import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CancelIcon from '@mui/icons-material/Cancel';
import { pavoApiService, type PavoDevice, type PavoPaymentRequest } from '../../../../services/pavoApiService';

interface PavoP2PDeviceSelectDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (deviceCode: string) => void;
  companyId?: string;
  branchId?: string;
  activePavoPayment?: PavoPaymentRequest | null;
  onCancelPayment?: () => Promise<void>;
  /**
   * Listelenecek cihazların `deviceMode` filtresi. Belirtilmemişse tüm
   * modlar gösterilir (varsayılan, eski davranış). Örneğin paket sipariş
   * ekranı yalnızca `pavo_order` cihazlarını kullanabilir → `['pavo_order']`.
   */
  allowedModes?: string[];
}

const deviceModeLabel = (mode?: string): string => {
  switch (mode) {
    case 'pavo_order': return 'Sipariş Terminali';
    case 'pavo_quicksale': return 'Hızlı Satış';
    case 'pavo_restaurant': return 'Restoran';
    default: return mode || '-';
  }
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);

const PavoP2PDeviceSelectDialog: React.FC<PavoP2PDeviceSelectDialogProps> = ({
  open,
  onClose,
  onSelect,
  companyId,
  branchId,
  activePavoPayment,
  onCancelPayment,
  allowedModes,
}) => {
  const [devices, setDevices] = useState<PavoDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // `allowedModes` prop'u her render'da yeni referans olabilir (`['pavo_order']`
  // gibi inline literal). useEffect bağımlılığı olarak kullanırken sonsuz
  // döngüye sokmayacak şekilde JSON imzasıyla stabilize ediyoruz.
  const allowedModesKey = allowedModes ? allowedModes.join(',') : '';

  const loadDevices = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const all = await pavoApiService.getPavoDevices(companyId, branchId);
      const filtered = allowedModes && allowedModes.length > 0
        ? all.filter((d) => d.deviceMode && allowedModes.includes(d.deviceMode))
        : all;
      filtered.sort((a, b) => (a.pavoNumber ?? 999) - (b.pavoNumber ?? 999));
      setDevices(filtered);
    } catch (e) {
      console.error('[PavoP2PDeviceSelect] Cihazlar yüklenemedi:', e);
      setDevices([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, branchId, allowedModesKey]);

  useEffect(() => {
    if (open && !activePavoPayment) loadDevices();
  }, [open, loadDevices, activePavoPayment]);

  const handleCancel = async () => {
    if (!onCancelPayment) return;
    setCancelling(true);
    try {
      await onCancelPayment();
      onClose();
    } finally {
      setCancelling(false);
    }
  };

  const onlineDevices = devices.filter((d) => d.isOnline);

  // Aktif ödeme varsa, ödeme durumunu göster
  if (activePavoPayment) {
    const ref = activePavoPayment.sourceReference || '';
    const parts = ref.includes('|') ? ref.split('|') : [ref];
    const tableName = parts[0]?.trim() || '-';
    const shortCode = parts.length > 1 ? parts[1]?.trim() : null;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PointOfSaleIcon sx={{ color: '#9C27B0' }} />
            <Typography variant="subtitle1" component="span" fontWeight={600}>
              Pavo Ödeme Durumu
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h5" fontWeight={700} color="#9C27B0" gutterBottom>
              {tableName}
            </Typography>
            {shortCode && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {shortCode}
              </Typography>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              {formatCurrency(activePavoPayment.totalAmount)}
            </Typography>
            <Chip
              label={activePavoPayment.status === 'pending' ? 'Bekliyor' : activePavoPayment.status === 'processing' ? 'İşleniyor' : activePavoPayment.status}
              color={activePavoPayment.status === 'pending' ? 'info' : 'warning'}
              sx={{ mt: 1 }}
            />
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
              Cihaz: {activePavoPayment.deviceCode.substring(0, 8)}...
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Button onClick={onClose} color="inherit">Kapat</Button>
          {onCancelPayment && (
            <Button
              variant="contained"
              color="error"
              startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
              onClick={handleCancel}
              disabled={cancelling}
            >
              Ödemeyi İptal Et
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  // Normal cihaz seçim modu
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PointOfSaleIcon sx={{ color: '#7c4dff' }} />
          <Typography variant="subtitle1" component="span" fontWeight={600}>
            Ödeme Gönderilecek Cihaz
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={loadDevices} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : onlineDevices.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {allowedModes && allowedModes.length > 0
                ? `${allowedModes.map(deviceModeLabel).join(' / ')} modunda online PavoPay cihazı bulunamadı.`
                : 'Online PavoPay cihazı bulunamadı.'}
            </Typography>
            {devices.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {devices.length} cihaz kayıtlı fakat hiçbiri online değil.
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {onlineDevices.map((device) => (
              <Card
                key={device.deviceCode}
                variant="outlined"
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
                }}
                onClick={() => onSelect(device.deviceCode)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: '#7c4dff',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 13,
                      flexShrink: 0,
                    }}
                  >
                    #{device.pavoNumber ?? '?'}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      Pavo #{device.pavoNumber ?? '?'} - {deviceModeLabel(device.deviceMode)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {device.deviceName} • {device.deviceCode}
                    </Typography>
                  </Box>
                  <Chip
                    icon={<FiberManualRecordIcon sx={{ fontSize: 8 }} />}
                    label="Online"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                </Box>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>İptal</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PavoP2PDeviceSelectDialog;
